import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Package, Truck, Receipt, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import client from '@/lib/api';
import PageHeader from '@/components/PageHeader';
import { formatCurrency, formatNumber } from '@/lib/format';
import { CardSkeleton } from '@/components/Skeletons';
import StatusBadge from '@/components/StatusBadge';

export default function WarehouseOverview() {
  const cQ = useQuery({ queryKey: ['wh-customers-count'], queryFn: () => client.get('/customers', { params: { limit: 1 } }).then((r) => r.data.meta.pagination.total) });
  const iQ = useQuery({ queryKey: ['wh-inv-count'], queryFn: () => client.get('/inventory', { params: { limit: 1 } }).then((r) => r.data.meta.pagination.total) });
  const dPending = useQuery({ queryKey: ['wh-dispatch-pending'], queryFn: () => client.get('/dispatch-requests', { params: { status: 'PENDING', limit: 100 } }).then((r) => r.data.data) });
  const invUnpaid = useQuery({ queryKey: ['wh-inv-unpaid'], queryFn: () => client.get('/invoices', { params: { status: 'UNPAID', limit: 100 } }).then((r) => r.data.data) });
  const recentDispatch = useQuery({ queryKey: ['wh-dispatch-recent'], queryFn: () => client.get('/dispatch-requests', { params: { limit: 6 } }).then((r) => r.data.data) });

  const unpaidTotal = (invUnpaid.data || []).reduce((s, i) => s + Number(i.grandTotal || 0), 0);

  return (
    <div data-testid="warehouse-overview-page">
      <PageHeader title="Warehouse dashboard" description="Live snapshot of customers, inventory, dispatch, and billing." />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi to="/warehouse/customers" icon={Users} label="Customers" value={cQ.isLoading ? '…' : formatNumber(cQ.data || 0)} sub="Registered businesses" testId="kpi-cust" />
        <Kpi to="/warehouse/inventory" icon={Package} label="Inventory batches" value={iQ.isLoading ? '…' : formatNumber(iQ.data || 0)} sub="Active SKUs" testId="kpi-inv" />
        <Kpi to="/warehouse/dispatch" icon={Truck} label="Pending dispatch" value={dPending.isLoading ? '…' : formatNumber((dPending.data || []).length)} sub="Awaiting approval" testId="kpi-disp" highlight={(dPending.data || []).length > 0} />
        <Kpi to="/warehouse/invoices" icon={Receipt} label="Unpaid invoices" value={invUnpaid.isLoading ? '…' : formatCurrency(unpaidTotal)} sub={`${(invUnpaid.data || []).length} invoice(s)`} testId="kpi-unpaid" />
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div>
            <h3 className="font-display font-semibold text-zinc-950">Recent dispatch activity</h3>
            <p className="text-xs text-zinc-500 mt-0.5">Latest requests across all customers</p>
          </div>
          <Link to="/warehouse/dispatch" className="text-xs text-zinc-950 hover:underline font-medium">View all →</Link>
        </div>
        <div className="divide-y divide-zinc-100" data-testid="wh-recent-dispatch">
          {recentDispatch.isLoading ? (
            <div className="p-4 space-y-2"><CardSkeleton height={40} /><CardSkeleton height={40} /><CardSkeleton height={40} /></div>
          ) : (recentDispatch.data || []).length === 0 ? (
            <div className="p-6 text-sm text-zinc-500">No dispatch activity yet.</div>
          ) : (recentDispatch.data || []).map((d) => (
            <Link to={`/warehouse/dispatch/${d.id}`} key={d.id} className="px-5 py-3 flex items-center justify-between hover:bg-zinc-50">
              <div>
                <div className="text-sm font-medium text-zinc-950">{d.customer?.businessName || 'Customer'}</div>
                <div className="text-xs text-zinc-500 font-mono">{(d.items || []).length} item(s) · Requested {formatDateOrNow(d.requestedAt)}</div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={d.status} />
                <ChevronRight className="w-4 h-4 text-zinc-400" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function formatDateOrNow(v) {
  if (!v) return '';
  const d = new Date(v);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Kpi({ icon: Icon, label, value, sub, to, highlight, testId }) {
  return (
    <Link to={to} className={`block bg-white border rounded-lg p-5 hover:shadow-sm hover:border-zinc-300 transition-all ${highlight ? 'border-yellow-300 bg-yellow-50/40' : 'border-zinc-200'}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-9 h-9 rounded-md bg-zinc-100 grid place-items-center">
          <Icon className="w-4 h-4 text-zinc-700" strokeWidth={1.75} />
        </div>
        <ChevronRight className="w-4 h-4 text-zinc-300" />
      </div>
      <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-1">{label}</div>
      <div className="font-display text-2xl font-bold text-zinc-950 tabular-nums">{value}</div>
      <div className="text-xs text-zinc-500 mt-1 font-mono">{sub}</div>
    </Link>
  );
}
